import type {
  ComparableCoolingObservationValue,
  ComparableDeliveryEventValue,
  ComparableObservation,
  ComparablePowerObservationValue,
  ComparableWaterObservationValue,
  ComparableWaterRecordStage,
  EconomicEvidenceBasis,
  EconomicSourceRef,
} from '../../types/economicContribution';
import type {
  EconomicResearchComparableAggregate,
  EconomicResearchComparableSummary,
  EconomicResearchEvidenceStep,
  EconomicResearchResultSnapshot,
} from '../../types/economicResearch';

export const COMPARABLE_PROJECT_FACETS = [
  'overview',
  'water',
  'cooling',
  'power',
  'delivery',
] as const;

export type ComparableProjectFacet = (typeof COMPARABLE_PROJECT_FACETS)[number];

export const COMPARABLE_WATER_STAGES = [
  'design-estimate',
  'application-request',
  'permit-limit',
  'reported-actual',
] as const satisfies readonly ComparableWaterRecordStage[];

export const NOT_FOUND_IN_REVIEWED_RECORDS = 'Not found in reviewed records';

export interface ComparableProjectSourceLink {
  label: string;
  url?: string;
  excerpt?: string;
  locator?: string;
}

export interface ComparableProjectDatum {
  id: string;
  label: string;
  value: string;
  kind: 'fact' | 'inference' | 'missing';
  asOf?: string;
  evidenceBasis?: Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>;
  sources?: readonly ComparableProjectSourceLink[];
}

export interface ComparableProjectWaterStagePresentation {
  stage: ComparableWaterRecordStage;
  label: string;
  observations: readonly ComparableProjectDatum[];
}

export interface ComparableProjectDeliveryChainPresentation {
  id: string;
  obstacle: ComparableProjectDatum;
  response: ComparableProjectDatum;
  outcome: ComparableProjectDatum;
}

export interface ComparableProjectPeerPresentation {
  projectId: string;
  facilityName: string;
  locationLabel: string;
  /** Retained for deterministic ordering and collapsed methodology only. */
  score: number;
  matchReasons: readonly string[];
  outcome: ComparableProjectDatum;
  coverage: Readonly<Record<ComparableProjectFacet, 'covered' | 'partial' | 'not-found'>>;
  facts: Readonly<Record<ComparableProjectFacet, readonly ComparableProjectDatum[]>>;
  inferences: Readonly<Record<ComparableProjectFacet, readonly ComparableProjectDatum[]>>;
  waterTimeline: readonly ComparableProjectWaterStagePresentation[];
  deliveryChains: readonly ComparableProjectDeliveryChainPresentation[];
  evidenceTrail: readonly EconomicResearchEvidenceStep[];
  reviewedSourceCount: number;
  updatedAt?: string;
}

export interface ComparableProjectsPresentationModel {
  peerCount: number;
  shownPeerCount: number;
  basisSummary: string;
  coverageLine: string;
  aggregates: readonly ComparableProjectAggregatePresentation[];
  /** Evidence-rich water/cooling peers, independently ranked from the general shortlist. */
  operationalPeers: readonly ComparableProjectPeerPresentation[];
  peers: readonly ComparableProjectPeerPresentation[];
}

export interface ComparableProjectAggregatePresentation {
  id: string;
  topic: EconomicResearchComparableAggregate['topic'];
  label: string;
  value: string;
  recordCount: number;
  method: NonNullable<EconomicResearchComparableAggregate['method']>;
}

const FACET_LABEL: Record<ComparableProjectFacet, string> = {
  overview: 'overview',
  water: 'water',
  cooling: 'cooling',
  power: 'power',
  delivery: 'delivery',
};

const WATER_STAGE_LABEL: Record<ComparableWaterRecordStage, string> = {
  'design-estimate': 'Design estimate',
  'application-request': 'Application request',
  'permit-limit': 'Service allocation / permit limit',
  'reported-actual': 'Reported actual',
};

const BASIS_LABEL: Record<Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>, string> = {
  'verified-actual': 'verified actual',
  'observed-public-record': 'observed public record',
  committed: 'committed',
  announced: 'announced',
  'secondary-discovery': 'discovery only',
};

function stableId(...parts: readonly (string | number | undefined)[]): string {
  return parts.filter((part) => part !== undefined).join(':').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function titleCase(value: string): string {
  return value.replace(/-/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function sourceLink(source: EconomicSourceRef): ComparableProjectSourceLink {
  return {
    label: `${source.publisher} · ${source.title}`,
    url: source.url,
    excerpt: source.excerpt,
    locator: source.locator,
  };
}

function sourceLinks(sources: readonly EconomicSourceRef[]): ComparableProjectSourceLink[] {
  return sources.map(sourceLink);
}

function observationId<T>(projectId: string, facet: ComparableProjectFacet, observation: ComparableObservation<T>, index: number): string {
  return observation.observationId ?? stableId(projectId, facet, observation.phaseBasis, observation.asOf, index);
}

function missingDatum(projectId: string, facet: ComparableProjectFacet, label: string): ComparableProjectDatum {
  return {
    id: stableId(projectId, facet, 'missing', label),
    label,
    value: NOT_FOUND_IN_REVIEWED_RECORDS,
    kind: 'missing',
  };
}

function locationLabel(record: EconomicResearchComparableSummary): string {
  return [record.locality, record.stateCode].filter(Boolean).join(', ') || record.stateCode;
}

function formatMw(record: EconomicResearchComparableSummary): string | null {
  if (!record.mw) return null;
  const basis = record.mw.basis === 'utility-service'
    ? 'utility service'
    : record.mw.basis === 'backup-generation'
      ? 'backup generation'
      : `${record.mw.basis} load`;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(record.mw.value)} MW ${basis}`;
}

function formatJobs(record: EconomicResearchComparableSummary): string | null {
  const jobs = record.permanentJobs;
  if (!jobs) return null;
  const range = jobs.low === jobs.high
    ? new Intl.NumberFormat('en-US').format(jobs.low)
    : `${new Intl.NumberFormat('en-US').format(jobs.low)}–${new Intl.NumberFormat('en-US').format(jobs.high)}`;
  return `${range} permanent onsite jobs`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

function formatNormalizedWaterRate(gallonsPerDay: number): string {
  if (gallonsPerDay >= 1_000_000) {
    return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(gallonsPerDay / 1_000_000)} MGD normalized average`;
  }
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(gallonsPerDay)} gal/day normalized average`;
}

const WATER_UNIT_LABEL: Record<ComparableWaterObservationValue['units'], string> = {
  'gal/day': 'gal/day',
  'million-gal/day': 'MGD',
  'acre-ft/year': 'acre-ft/year',
  'liters/day': 'L/day',
  gallons: 'gal',
  'million-gallons': 'million gal',
  'acre-ft': 'acre-ft',
  liters: 'L',
};

function formatWaterValue(value: ComparableWaterObservationValue): string {
  const source = value.waterSource && value.waterSource !== 'unknown' ? ` · ${titleCase(value.waterSource)}` : '';
  const normalizedAverage = value.normalizedGallonsPerDay != null
    && Number.isFinite(value.normalizedGallonsPerDay)
    && value.units !== 'gal/day'
    && value.units !== 'million-gal/day'
      ? ` · ${formatNormalizedWaterRate(value.normalizedGallonsPerDay)}`
      : '';
  return `${formatNumber(value.amount)} ${WATER_UNIT_LABEL[value.units]} ${value.flow} · ${titleCase(value.statistic)} · ${titleCase(value.boundary)}${source}${normalizedAverage}`;
}

function formatCoolingValue(value: ComparableCoolingObservationValue): string {
  const parts = [
    value.rackCoolingStrategy && value.rackCoolingStrategy !== 'unknown' && value.rackCoolingStrategy !== 'undecided'
      ? `Rack: ${titleCase(value.rackCoolingStrategy)}`
      : null,
    value.heatRejection && value.heatRejection !== 'unknown'
      ? `Heat rejection: ${titleCase(value.heatRejection)}`
      : null,
    value.coolingGeneration && value.coolingGeneration !== 'unknown'
      ? `Cooling plant: ${titleCase(value.coolingGeneration)}`
      : null,
    value.scope !== 'unknown' ? `Scope: ${titleCase(value.scope)}` : null,
    value.nativeDescription?.trim() ? `Source wording: ${value.nativeDescription.trim()}` : null,
  ].filter(Boolean);
  return parts.join(' · ') || NOT_FOUND_IN_REVIEWED_RECORDS;
}

function formatPowerValue(value: ComparablePowerObservationValue): string {
  const parts = [
    value.capacityMw !== undefined
      ? `${formatNumber(value.capacityMw)} MW${value.capacityBasis ? ` ${titleCase(value.capacityBasis)}` : ''}`
      : null,
    value.technologies.length > 0 ? value.technologies.map(titleCase).join(', ') : null,
    value.utility ? `Utility: ${value.utility}` : null,
    value.isoRto ? `Grid: ${value.isoRto}` : null,
    value.docketOrAgreementId ? `Record: ${value.docketOrAgreementId}` : null,
  ].filter(Boolean);
  return parts.join(' · ') || NOT_FOUND_IN_REVIEWED_RECORDS;
}

function eventDate(observation: ComparableObservation<ComparableDeliveryEventValue>): string {
  return observation.value.occurredOn || observation.asOf || '';
}

function defaultFacts(record: EconomicResearchComparableSummary): Record<ComparableProjectFacet, ComparableProjectDatum[]> {
  const overview: ComparableProjectDatum[] = [{
    id: stableId(record.projectId, 'overview', 'location'),
    label: 'Location',
    value: locationLabel(record),
    kind: 'fact',
  }];
  const power: ComparableProjectDatum[] = [];
  const mw = formatMw(record);
  if (mw) {
    const capacity: ComparableProjectDatum = {
      id: stableId(record.projectId, 'power', 'documented-capacity'),
      label: 'Documented capacity',
      value: mw,
      kind: 'fact',
      ...(record.mw?.sources?.length ? { sources: sourceLinks(record.mw.sources) } : {}),
    };
    overview.push(capacity);
    power.push(capacity);
  }
  const jobs = formatJobs(record);
  if (jobs && record.permanentJobs) {
    overview.push({
      id: stableId(record.projectId, 'overview', 'permanent-jobs'),
      label: 'Permanent onsite employment',
      value: jobs,
      kind: 'fact',
      evidenceBasis: record.permanentJobs.evidenceBasis,
      ...(record.permanentJobs.sources?.length ? { sources: sourceLinks(record.permanentJobs.sources) } : {}),
    });
  }
  return { overview, water: [], cooling: [], power, delivery: [] };
}

function defaultInferences(record: EconomicResearchComparableSummary): Record<ComparableProjectFacet, ComparableProjectDatum[]> {
  const overview = record.reasons.map((reason, index): ComparableProjectDatum => ({
    id: stableId(record.projectId, 'overview', 'match-reason', index),
    label: index === 0 ? 'Why this project compares' : 'Additional match reason',
    value: reason,
    kind: 'inference',
  }));
  if (record.incompatibilities.length > 0) {
    overview.push({
      id: stableId(record.projectId, 'overview', 'comparison-limit'),
      label: 'Comparison limit',
      value: record.incompatibilities.join(' · '),
      kind: 'inference',
    });
  }
  return { overview, water: [], cooling: [], power: [], delivery: [] };
}

function mapCoolingFacts(record: EconomicResearchComparableSummary): ComparableProjectDatum[] {
  return (record.coolingObservations ?? []).map((observation, index) => ({
    id: observationId(record.projectId, 'cooling', observation, index),
    label: `Cooling configuration · ${titleCase(observation.phaseBasis)}`,
    value: formatCoolingValue(observation.value),
    kind: 'fact',
    ...(observation.asOf ? { asOf: observation.asOf } : {}),
    evidenceBasis: observation.evidenceBasis,
    ...(observation.sources.length > 0 ? { sources: sourceLinks(observation.sources) } : {}),
  }));
}

function mapPowerFacts(record: EconomicResearchComparableSummary): ComparableProjectDatum[] {
  return (record.powerObservations ?? []).map((observation, index) => ({
    id: observationId(record.projectId, 'power', observation, index),
    label: `${titleCase(observation.value.role)} · ${titleCase(observation.value.status)}`,
    value: formatPowerValue(observation.value),
    kind: 'fact',
    ...(observation.asOf ? { asOf: observation.asOf } : {}),
    evidenceBasis: observation.evidenceBasis,
    ...(observation.sources.length > 0 ? { sources: sourceLinks(observation.sources) } : {}),
  }));
}

function mapDeliveryFacts(record: EconomicResearchComparableSummary): ComparableProjectDatum[] {
  return [...(record.deliveryEvents ?? [])]
    .sort((left, right) => eventDate(left).localeCompare(eventDate(right)))
    .map((observation, index) => ({
      id: observationId(record.projectId, 'delivery', observation, index),
      label: `${titleCase(observation.value.eventType)} · ${titleCase(observation.value.category)}`,
      value: observation.value.summary,
      kind: 'fact',
      asOf: observation.value.occurredOn,
      evidenceBasis: observation.evidenceBasis,
      ...(observation.sources.length > 0 ? { sources: sourceLinks(observation.sources) } : {}),
    }));
}

function waterTimeline(record: EconomicResearchComparableSummary): ComparableProjectWaterStagePresentation[] {
  return COMPARABLE_WATER_STAGES.map((stage) => {
    const observations = [...(record.waterTimeline ?? [])]
      .filter((observation) => observation.value.stage === stage)
      .sort((left, right) => (left.asOf ?? left.value.periodEnd ?? '').localeCompare(right.asOf ?? right.value.periodEnd ?? ''))
      .map((observation, index): ComparableProjectDatum => ({
        id: observationId(record.projectId, 'water', observation, index),
        label: `${titleCase(observation.value.flow)} · ${titleCase(observation.value.statistic)}`,
        value: formatWaterValue(observation.value),
        kind: 'fact',
        ...(observation.asOf || observation.value.periodEnd
          ? { asOf: observation.asOf ?? observation.value.periodEnd }
          : {}),
        evidenceBasis: observation.evidenceBasis,
        ...(observation.sources.length > 0 ? { sources: sourceLinks(observation.sources) } : {}),
      }));
    return {
      stage,
      label: WATER_STAGE_LABEL[stage],
      observations: observations.length > 0
        ? observations
        : [missingDatum(record.projectId, 'water', WATER_STAGE_LABEL[stage])],
    };
  });
}

function deliveryDatum(
  projectId: string,
  label: string,
  observation: ComparableObservation<ComparableDeliveryEventValue> | undefined,
  index: number,
): ComparableProjectDatum {
  if (!observation) return missingDatum(projectId, 'delivery', label);
  return {
    id: stableId(projectId, 'delivery-chain', label, observation.observationId, index),
    label,
    value: observation.value.summary,
    // The dated event is factual; interpreting its role in this chain is not.
    kind: 'inference',
    asOf: observation.value.occurredOn,
    evidenceBasis: observation.evidenceBasis,
    ...(observation.sources.length > 0 ? { sources: sourceLinks(observation.sources) } : {}),
  };
}

function isObstacle(value: ComparableDeliveryEventValue): boolean {
  return value.effect === 'delayed'
    || value.effect === 'constrained'
    || value.effect === 'stopped'
    || ['permit-denied', 'approval-denied', 'construction-delayed', 'capacity-reduced', 'project-cancelled', 'legal-challenge-filed', 'public-opposition-recorded'].includes(value.eventType);
}

function isOutcome(value: ComparableDeliveryEventValue): boolean {
  return value.effect === 'advanced'
    || ['permit-issued', 'approval-granted', 'agreement-executed', 'service-energized', 'operations-started', 'project-cancelled', 'legal-challenge-resolved'].includes(value.eventType);
}

function relatedEvents(
  anchor: ComparableObservation<ComparableDeliveryEventValue>,
  events: readonly ComparableObservation<ComparableDeliveryEventValue>[],
): ComparableObservation<ComparableDeliveryEventValue>[] {
  const anchorId = anchor.observationId;
  const linked = new Set(anchor.value.linkedEventIds ?? []);
  return events.filter((candidate) => {
    const candidateId = candidate.observationId;
    return Boolean(
      (candidateId && linked.has(candidateId))
      || (anchorId && candidate.value.linkedEventIds?.includes(anchorId)),
    );
  }).sort((left, right) => eventDate(left).localeCompare(eventDate(right)));
}

function deliveryChains(record: EconomicResearchComparableSummary): ComparableProjectDeliveryChainPresentation[] {
  const events = [...(record.deliveryEvents ?? [])].sort((left, right) => eventDate(left).localeCompare(eventDate(right)));
  const obstacles = events.filter((event) => isObstacle(event.value));
  if (obstacles.length === 0) {
    if (events.length === 0) {
      return [{
        id: stableId(record.projectId, 'delivery', 'chain', 0),
        obstacle: missingDatum(record.projectId, 'delivery', 'Obstacle'),
        response: missingDatum(record.projectId, 'delivery', 'Response / mitigation'),
        outcome: missingDatum(record.projectId, 'delivery', 'Realized outcome'),
      }];
    }
    const outcome = [...events].reverse().find((event) => isOutcome(event.value)) ?? events.at(-1);
    return [{
      id: stableId(record.projectId, 'delivery', 'chain', 'outcome-only'),
      obstacle: missingDatum(record.projectId, 'delivery', 'Obstacle'),
      response: missingDatum(record.projectId, 'delivery', 'Response / mitigation'),
      outcome: deliveryDatum(record.projectId, 'Realized outcome', outcome, 0),
    }];
  }
  return obstacles.slice(0, 3).map((obstacle, index) => {
    const linked = relatedEvents(obstacle, events);
    const later = linked.filter((event) => eventDate(event) >= eventDate(obstacle));
    const outcome = [...later].reverse().find((event) => isOutcome(event.value));
    const response = later.find((event) => event !== outcome && event !== obstacle);
    return {
      id: stableId(record.projectId, 'delivery', 'chain', obstacle.observationId, index),
      obstacle: deliveryDatum(record.projectId, 'Obstacle', obstacle, index * 3),
      response: deliveryDatum(record.projectId, 'Response / mitigation', response, index * 3 + 1),
      outcome: deliveryDatum(record.projectId, 'Realized outcome', outcome, index * 3 + 2),
    };
  });
}

function allObservationSources(record: EconomicResearchComparableSummary): EconomicSourceRef[] {
  return [
    ...(record.mw?.sources ?? []),
    ...(record.permanentJobs?.sources ?? []),
    ...[
      ...(record.waterTimeline ?? []),
      ...(record.coolingObservations ?? []),
      ...(record.powerObservations ?? []),
      ...(record.deliveryEvents ?? []),
    ].flatMap((observation) => observation.sources),
  ];
}

function combinedEvidenceTrail(record: EconomicResearchComparableSummary): EconomicResearchEvidenceStep[] {
  const knownObservationClaims = new Set(record.evidenceTrail
    .filter((step) => step.kind === 'official-record')
    .map((step) => `${step.sourceUrl ?? ''}|${step.excerpt ?? ''}|${step.locator ?? ''}`));
  const additional = new Map<string, EconomicResearchEvidenceStep>();
  for (const source of allObservationSources(record)) {
    const claimIdentity = `${source.url}|${source.excerpt}|${source.locator}`;
    if (knownObservationClaims.has(claimIdentity)) continue;
    additional.set(claimIdentity, {
      kind: 'official-record',
      label: source.publisher,
      detail: source.title,
      sourceUrl: source.url,
      excerpt: source.excerpt,
      locator: source.locator,
    });
  }
  return [...record.evidenceTrail, ...additional.values()];
}

function facetCoverage(
  facts: Readonly<Record<ComparableProjectFacet, readonly ComparableProjectDatum[]>>,
  timeline: readonly ComparableProjectWaterStagePresentation[],
): Record<ComparableProjectFacet, 'covered' | 'partial' | 'not-found'> {
  const foundWaterStages = timeline.filter((stage) => stage.observations.some((item) => item.kind === 'fact')).length;
  return {
    overview: facts.overview.length > 0 ? 'covered' : 'not-found',
    water: foundWaterStages === COMPARABLE_WATER_STAGES.length ? 'covered' : foundWaterStages > 0 ? 'partial' : 'not-found',
    cooling: facts.cooling.length > 0 ? 'covered' : 'not-found',
    power: facts.power.length > 0 ? 'covered' : 'not-found',
    delivery: facts.delivery.length > 0 ? 'covered' : 'not-found',
  };
}

function latestObservationDate(record: EconomicResearchComparableSummary): string | undefined {
  return [
    ...(record.waterTimeline ?? []).flatMap((observation) => [observation.asOf, observation.value.periodEnd]),
    ...(record.coolingObservations ?? []).map((observation) => observation.asOf),
    ...(record.powerObservations ?? []).map((observation) => observation.asOf),
    ...(record.deliveryEvents ?? []).map((observation) => observation.value.occurredOn),
  ].filter((value): value is string => Boolean(value)).sort().at(-1);
}

function peerPresentation(record: EconomicResearchComparableSummary): ComparableProjectPeerPresentation {
  const facts = defaultFacts(record);
  const inferences = defaultInferences(record);
  const timeline = waterTimeline(record);
  facts.cooling.push(...mapCoolingFacts(record));
  facts.power.push(...mapPowerFacts(record));
  facts.delivery.push(...mapDeliveryFacts(record));
  const chains = deliveryChains(record);
  const latestEvent = [...(record.deliveryEvents ?? [])].sort((left, right) => eventDate(left).localeCompare(eventDate(right))).at(-1);
  const outcome = latestEvent
    ? deliveryDatum(record.projectId, 'Latest documented outcome', latestEvent, 999)
    : missingDatum(record.projectId, 'delivery', 'Project outcome');
  const evidenceTrail = combinedEvidenceTrail(record);
  const reviewedSourceCount = new Set(evidenceTrail
    .filter((step) => step.kind === 'official-record')
    .map((step) => step.sourceUrl ?? `${step.label}:${step.detail}`)).size;
  return {
    projectId: record.projectId,
    facilityName: record.facilityName,
    locationLabel: locationLabel(record),
    score: record.score,
    matchReasons: record.reasons,
    outcome,
    coverage: facetCoverage(facts, timeline),
    facts,
    inferences,
    waterTimeline: timeline,
    deliveryChains: chains,
    evidenceTrail,
    reviewedSourceCount,
    ...(latestObservationDate(record) ? { updatedAt: latestObservationDate(record) } : {}),
  };
}

function basisSummary(snapshot: EconomicResearchResultSnapshot): string {
  const parts = Object.entries(snapshot.basisCounts)
    .filter((entry): entry is [Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>, number] => (
      typeof entry[1] === 'number' && entry[1] > 0
    ))
    .map(([basis, count]) => `${count} ${BASIS_LABEL[basis] ?? basis}`);
  return parts.join(' · ') || 'Evidence basis not stated';
}

function aggregateLabel(topic: EconomicResearchComparableAggregate['topic'], key: string): string {
  const scoped = key.split('|').map((part) => titleCase(part.replace(':', ': '))).join(' · ');
  return `${titleCase(topic)} · ${scoped}`;
}

function aggregatePresentation(
  aggregates: readonly EconomicResearchComparableAggregate[] | undefined,
): ComparableProjectAggregatePresentation[] {
  return (aggregates ?? []).flatMap((aggregate) => {
    const method = aggregate.method;
    if (!method) return [];
    return [
      ...aggregate.numeric.map((entry) => ({
        id: stableId('aggregate', aggregate.topic, entry.key),
        topic: aggregate.topic,
        label: aggregateLabel(aggregate.topic, entry.key),
        value: `${formatNumber(entry.low)}–${formatNumber(entry.high)} ${entry.units} · median ${formatNumber(entry.median)}`,
        recordCount: entry.recordCount,
        method,
      })),
      ...aggregate.categorical.map((entry) => ({
        id: stableId('aggregate', aggregate.topic, entry.key),
        topic: aggregate.topic,
        label: aggregateLabel(aggregate.topic, entry.key),
        value: `Observed in ${entry.recordCount} compatible projects`,
        recordCount: entry.recordCount,
        method,
      })),
    ];
  });
}

const OPERATIONAL_SHORTLIST_FACETS = ['water', 'cooling', 'power', 'delivery'] as const;
type OperationalShortlistFacet = (typeof OPERATIONAL_SHORTLIST_FACETS)[number];

function summaryOperationalFacets(record: EconomicResearchComparableSummary): ReadonlySet<OperationalShortlistFacet> {
  const facets = new Set<OperationalShortlistFacet>();
  if ((record.waterTimeline?.length ?? 0) > 0) facets.add('water');
  if ((record.coolingObservations?.length ?? 0) > 0) facets.add('cooling');
  // A scalar MW value answers scale, not the requested power-source/type
  // question. Only a structured power observation counts here.
  if ((record.powerObservations?.length ?? 0) > 0) facets.add('power');
  if ((record.deliveryEvents?.length ?? 0) > 0) facets.add('delivery');
  return facets;
}

/**
 * Keep the two strongest compatibility matches, then use the final dossier
 * slot for a policy-valid peer that closes the largest operational evidence
 * gap. This prevents a low-MW/jobs-score water or cooling record from being
 * hidden while retaining the workforce screen's ranking decisions.
 */
export function selectComparableProjectSummaries(
  records: readonly EconomicResearchComparableSummary[],
  limit = 3,
): EconomicResearchComparableSummary[] {
  if (limit <= 0) return [];
  const ranked = [...records]
    .sort((left, right) => right.score - left.score || left.facilityName.localeCompare(right.facilityName));
  if (ranked.length <= limit || limit < 3) return ranked.slice(0, limit);

  const anchors = ranked.slice(0, limit - 1);
  const covered = new Set<OperationalShortlistFacet>();
  for (const record of anchors) {
    for (const facet of summaryOperationalFacets(record)) covered.add(facet);
  }
  const candidates = ranked.slice(limit - 1)
    .map((record) => ({
      record,
      coverageGain: [...summaryOperationalFacets(record)].filter((facet) => !covered.has(facet)).length,
    }))
    .sort((left, right) => (
      right.coverageGain - left.coverageGain
      || right.record.score - left.record.score
      || left.record.facilityName.localeCompare(right.record.facilityName)
    ));
  return [...anchors, candidates[0]!.record];
}

/**
 * Water and cooling comparisons need an evidence-specific cohort. A strong
 * jobs/MW match with no operating-system record must not displace the few
 * peers that actually document water stage or cooling infrastructure.
 */
export function selectOperationalEvidenceSummaries(
  records: readonly EconomicResearchComparableSummary[],
  limit = 5,
): EconomicResearchComparableSummary[] {
  if (limit <= 0) return [];
  return records
    .filter((record) => (
      (record.waterTimeline?.length ?? 0) > 0
      || (record.coolingObservations?.length ?? 0) > 0
    ))
    .sort((left, right) => {
      const rank = (record: EconomicResearchComparableSummary): number => {
        const hasWater = (record.waterTimeline?.length ?? 0) > 0;
        const hasCooling = (record.coolingObservations?.length ?? 0) > 0;
        const hasReportedActual = record.waterTimeline?.some((observation) => (
          observation.value.stage === 'reported-actual'
        )) ?? false;
        const hasSameStateContext = record.reasons.some((reason) => /same[- ]state context/iu.test(reason));
        return Number(hasSameStateContext) * 1_000
          + Number(hasWater && hasCooling) * 300
          + Number(hasReportedActual) * 100
          + Number(hasWater) * 30
          + Number(hasCooling) * 20
          + record.score;
      };
      return rank(right) - rank(left) || left.facilityName.localeCompare(right.facilityName);
    })
    .slice(0, limit);
}

export function buildComparableProjectsPresentation(
  snapshot: EconomicResearchResultSnapshot | null | undefined,
): ComparableProjectsPresentationModel | null {
  if (!snapshot || snapshot.policyValidRecords.length === 0) return null;
  const peers = selectComparableProjectSummaries(snapshot.policyValidRecords).map(peerPresentation);
  const operationalPeers = selectOperationalEvidenceSummaries(snapshot.policyValidRecords).map(peerPresentation);
  const coverageParts = COMPARABLE_PROJECT_FACETS
    .filter((facet) => facet !== 'overview')
    .map((facet) => {
      const covered = peers.filter((peer) => peer.coverage[facet] !== 'not-found').length;
      return `${covered}/${peers.length} ${FACET_LABEL[facet]}`;
    });
  return {
    peerCount: snapshot.policyValidRecords.length,
    shownPeerCount: peers.length,
    basisSummary: basisSummary(snapshot),
    coverageLine: coverageParts.join(' · '),
    aggregates: aggregatePresentation(snapshot.comparableAggregates),
    operationalPeers,
    peers,
  };
}
